/**
 * WarehouseItemDetailPanel
 * ────────────────────────
 * Single warehouse-item detail + item-contextual write actions (3b-ii).
 * Sections: Item, Dwell, Current Stay, Prior Stays (history). Fetches the
 * authoritative doc via getWarehouseItemDetail so dwell_days + full stays[]
 * are present.
 *
 * Action bar (ADMIN/FIELD_REP only) shows buttons BY ITEM STATE, mirroring the
 * backend preconditions so it never offers an action the backend rejects:
 *   Verify   — when verified=false
 *   Hold     — always (Place when hold=false, Release when hold=true)
 *   Put Away — when warehouse_status=STAGED_INBOUND
 *   Move     — when warehouse_status=STORED (backend move is STORED-only)
 *   Ship     — when an open stay exists; ENABLED only when verified=true,
 *              otherwise disabled with a "verify first" hint (the 409 gate)
 * Terminal (TRANSFERRED_OUT/SCRAPPED/SOLD) and ON_LOAN have no open stay →
 * only Hold remains. Each action opens a ModalShell form, calls
 * manage_warehouse_item, then re-fetches detail + asks the page to refresh the
 * list (onChanged), with a success toast.
 *
 * Dates are displayed as RAW strings — never new Date() on YYYY-MM-DD. Dwell is
 * the API dwell_days; never recomputed. shipped_date defaults to today via
 * toLocaleDateString('en-CA') (the house no-drift YYYY-MM-DD generator).
 */

import React, { useCallback, useEffect, useState } from 'react';
import ModalShell from '../../../shared/components/ModalShell';
import { useUser } from '../../../shared/context/UserContext';
import {
  getWarehouseItemDetail, setVerified, setHold,
  putAwayWarehouseItem, moveWarehouseItem, shipWarehouseItem,
} from '../api/warehouseApi';
import WarehouseLocationFields from './WarehouseLocationFields';

const WRITE_ROLES = new Set(['ADMIN', 'FIELD_REP']);
const SHIP_DISPOSITIONS = ['ON_LOAN', 'TRANSFERRED_OUT', 'SCRAPPED', 'SOLD'];
const EMPTY_LOC = { building: '', section: '', row: '', stack: '' };

const raw = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));
const yesNo = (v) => (v === true ? 'Yes' : v === false ? 'No' : '—');
const todayLocal = () => new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD, no drift

function joinLocation(loc) {
  if (!loc) return '—';
  const parts = [loc.building, loc.section, loc.row, loc.stack].filter(Boolean);
  return parts.length ? parts.join(' / ') : '—';
}

function Field({ label, value }) {
  return (
    <div className="wh-field">
      <span className="wh-field-label">{label}</span>
      <span className="wh-field-value">{value}</span>
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div className="wh-section">
      <div className="wh-section-header"><span className="wh-section-title">{title}</span></div>
      <div className="wh-section-body">{children}</div>
    </div>
  );
}

export default function WarehouseItemDetailPanel({ callerEmail, siteId, warehouseItemId, onClose, onChanged }) {
  const { userProfile } = useUser();
  const canWrite = WRITE_ROLES.has(userProfile?.role);

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  /* Modal: null | 'verify' | 'hold' | 'putaway' | 'move' | 'ship' */
  const [modal, setModal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState(null);

  /* STABLE close handler. ModalShell's browser-back effect depends on onClose;
     a fresh arrow each render would thrash that effect and its cleanup's
     history.back() would fire popstate → onClose → close the modal on the first
     keystroke. Keep one identity. */
  const closeModal = useCallback(() => setModal(null), []);

  /* Per-modal form state */
  const [holdReason, setHoldReason] = useState('');
  const [loc, setLoc] = useState(EMPTY_LOC);
  const [shipDisp, setShipDisp] = useState('ON_LOAN');
  const [shippedDate, setShippedDate] = useState(todayLocal());
  const [shipRef, setShipRef] = useState('');

  const reload = useCallback(() => {
    if (!callerEmail || !siteId || !warehouseItemId) return Promise.resolve();
    return getWarehouseItemDetail(callerEmail, { siteId, warehouseItemId })
      .then((doc) => setItem(doc))
      .catch((err) => setError(err.message));
  }, [callerEmail, siteId, warehouseItemId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setToast(null);
    getWarehouseItemDetail(callerEmail, { siteId, warehouseItemId })
      .then((doc) => { if (!cancelled) setItem(doc); })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [callerEmail, siteId, warehouseItemId]);

  const stays = item?.stays || [];
  const openStay = stays.find((s) => s.open) || null;
  const priorStays = stays.filter((s) => !s.open);
  const status = item?.warehouse_status;
  const verified = item?.verified === true;
  const onHold = item?.hold === true;

  function openModal(name) {
    setModalError(null);
    if (name === 'hold') setHoldReason('');
    if (name === 'putaway') setLoc(EMPTY_LOC);
    if (name === 'move') {
      setLoc({
        building: item?.building || '', section: item?.section || '',
        row: item?.row || '', stack: item?.stack || '',
      });
    }
    if (name === 'ship') { setShipDisp('ON_LOAN'); setShippedDate(todayLocal()); setShipRef(''); }
    setModal(name);
  }

  async function run(fn, successMsg) {
    setSubmitting(true);
    setModalError(null);
    try {
      await fn();
      setModal(null);
      await reload();
      if (onChanged) onChanged();
      setToast(successMsg);
    } catch (err) {
      setModalError(err.message || 'Action failed.');
    } finally {
      setSubmitting(false);
    }
  }

  const doVerify = () => run(
    () => setVerified(callerEmail, { siteId, warehouseItemId }), 'Item verified.');
  const doHold = () => run(
    () => setHold(callerEmail, { siteId, warehouseItemId, hold: !onHold, holdReason: !onHold ? holdReason.trim() : '' }),
    onHold ? 'Hold released.' : 'Hold placed.');
  const doPutAway = () => run(
    () => putAwayWarehouseItem(callerEmail, { siteId, warehouseItemId, location: loc }), 'Item stored.');
  const doMove = () => run(
    () => moveWarehouseItem(callerEmail, { siteId, warehouseItemId, location: loc }), 'Item moved.');
  const doShip = () => run(
    () => shipWarehouseItem(callerEmail, { siteId, warehouseItemId, shipDisposition: shipDisp, shippedDate, shipRef: shipRef.trim() }),
    `Shipped (${shipDisp}).`);

  /* STORED-without-location guard (soft, client-side). Shared loc state; empty =
     all four tiers blank (trimmed). Gates Put-away (→STORED) and Move (STORED). */
  const locEmpty = ![loc.building, loc.section, loc.row, loc.stack].some((v) => (v || '').trim());

  const modalFooter = (onConfirm, confirmLabel, danger, extraDisabled = false) => (
    <>
      <button type="button" className="wh-btn-secondary" onClick={closeModal} disabled={submitting}>Cancel</button>
      <button type="button" className={danger ? 'wh-btn-danger' : 'wh-btn-primary'} onClick={onConfirm} disabled={submitting || extraDisabled}>
        {submitting ? 'Working…' : confirmLabel}
      </button>
    </>
  );

  return (
    <div className="wh-detail">
      <div className="wh-detail-head">
        <button className="wh-back" onClick={onClose} type="button">&larr; Back</button>
        <h2 className="wh-detail-title">{warehouseItemId}</h2>
      </div>

      {toast && <div className="wh-form-toast">{toast}</div>}
      {loading && <div className="wh-detail-status">Loading…</div>}
      {error && <div className="wh-detail-status wh-error">{error}</div>}

      {!loading && !error && item && (
        <>
          {/* ── Action bar (state-driven; ADMIN/FIELD_REP) ───────────── */}
          {canWrite && (
            <div className="wh-actionbar">
              {!verified && <button className="wh-action-btn" onClick={() => openModal('verify')}>Verify</button>}
              <button className="wh-action-btn" onClick={() => openModal('hold')}>{onHold ? 'Release Hold' : 'Place Hold'}</button>
              {status === 'STAGED_INBOUND' && <button className="wh-action-btn" onClick={() => openModal('putaway')}>Put Away</button>}
              {status === 'STORED' && <button className="wh-action-btn" onClick={() => openModal('move')}>Move</button>}
              {openStay && (
                <button
                  className="wh-action-btn"
                  onClick={() => openModal('ship')}
                  disabled={!verified}
                  title={!verified ? 'Verify before shipping.' : undefined}
                >
                  Ship
                </button>
              )}
              {openStay && !verified && <span className="wh-action-hint">Verify before shipping.</span>}
            </div>
          )}

          <div className="wh-detail-body">
            <Section title="Item">
              <Field label="Item ID" value={raw(item.warehouse_item_id)} />
              <Field label="Asset Type" value={raw(item.asset_type)} />
              <Field label="Tracking Mode" value={raw(item.tracking_mode)} />
              <Field label="Quantity" value={raw(item.quantity)} />
              <Field label="Contract" value={raw(item.contract_id)} />
              <Field label="Customer PO" value={raw(item.contract?.customer_po)} />
              <Field label="Status" value={raw(item.warehouse_status)} />
              <Field label="Link Mode" value={raw(item.link_mode)} />
              <Field
                label="Linked Tool"
                value={item.linked_tool_supplier_id
                  ? `${item.linked_tool_supplier_id} · from ${item.linked_tool_site_name || '—'}`
                  : raw(item.linked_tool_id)}
              />
              <Field label="Verified" value={yesNo(item.verified)} />
              <Field label="Hold" value={yesNo(item.hold)} />
              {item.hold && <Field label="Hold Reason" value={raw(item.hold_reason)} />}
            </Section>

            <Section title="Dwell">
              <Field label="Dwell (days)" value={item.dwell_days === null || item.dwell_days === undefined ? '—' : `${item.dwell_days}`} />
              <Field
                label="Dwell (years)"
                value={item.dwell?.dwell_years === null || item.dwell?.dwell_years === undefined
                  ? 'On loan'
                  : `${Number(item.dwell.dwell_years).toFixed(1)}y`}
              />
              <Field
                label="Threshold"
                value={item.dwell?.threshold_source === 'NONE'
                  || item.dwell?.threshold_years === null
                  || item.dwell?.threshold_years === undefined
                  ? 'No policy'
                  : `${Number(item.dwell.threshold_years).toFixed(1)}y (${item.dwell.threshold_source})`}
              />
              <Field label="Dwell crossed" value={yesNo(item.dwell?.dwell_crossed)} />
              <Field label="Auto-disposition" value={yesNo(item.dwell?.auto_disposition_enabled)} />
              <Field label="Disposition status" value={raw(item.dwell_disposition_status)} />
              <Field label="Current Received Date" value={raw(item.current_received_date)} />
            </Section>

            <Section title="Current Stay">
              {openStay ? (
                <>
                  <Field label="Received Date" value={raw(openStay.received_date)} />
                  <Field label="Intake Mode" value={raw(openStay.intake_mode)} />
                  <Field label="Received By" value={raw(openStay.received_by)} />
                  <Field label="Location" value={joinLocation(openStay.received_location)} />
                  {Array.isArray(openStay.moves) && openStay.moves.length > 0 && (
                    <div className="wh-moves">
                      <span className="wh-field-label">Moves</span>
                      {openStay.moves.map((m, i) => (
                        <div key={i} className="wh-move-row">
                          {raw(m.moved_date)}: {joinLocation(m.from)} &rarr; {joinLocation(m.to)}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="wh-empty">No open stay — item is not currently on-site.</div>
              )}
            </Section>

            <Section title={`Prior Stays (${priorStays.length})`}>
              {priorStays.length === 0 ? (
                <div className="wh-empty">No prior stays.</div>
              ) : (
                priorStays.map((s, i) => (
                  <div key={s.stay_id || i} className="wh-stay-card">
                    <div className="wh-stay-line"><strong>Received</strong> {raw(s.received_date)} ({raw(s.intake_mode)})</div>
                    <div className="wh-stay-line">
                      <strong>Shipped</strong> {raw(s.shipped_date)} · {raw(s.ship_disposition)}{s.ship_ref ? ` · ${s.ship_ref}` : ''}
                    </div>
                    {Array.isArray(s.moves) && s.moves.length > 0 && (
                      <div className="wh-stay-line wh-stay-moves">{s.moves.length} move{s.moves.length === 1 ? '' : 's'}</div>
                    )}
                  </div>
                ))
              )}
            </Section>
          </div>
        </>
      )}

      {/* ── Modals ───────────────────────────────────────────────────── */}
      <ModalShell isOpen={modal === 'verify'} onClose={closeModal} title="Verify Item"
        footer={modalFooter(doVerify, 'Verify')}>
        {modalError && <div className="wh-form-error">{modalError}</div>}
        <p className="wh-modal-text">Mark this item verified? Verified items can be shipped.</p>
      </ModalShell>

      <ModalShell isOpen={modal === 'hold'} onClose={closeModal} title={onHold ? 'Release Hold' : 'Place Hold'}
        footer={modalFooter(doHold, onHold ? 'Release Hold' : 'Place Hold')}>
        {modalError && <div className="wh-form-error">{modalError}</div>}
        {onHold ? (
          <p className="wh-modal-text">Release the hold on this item?</p>
        ) : (
          <label className="wh-field-row">
            <span className="wh-label">Hold Reason</span>
            <input className="wh-input" type="text" value={holdReason} onChange={(e) => setHoldReason(e.target.value)} placeholder="Optional" />
          </label>
        )}
      </ModalShell>

      <ModalShell isOpen={modal === 'putaway'} onClose={closeModal} title="Put Away"
        footer={modalFooter(doPutAway, 'Put Away', false, locEmpty)}>
        {modalError && <div className="wh-form-error">{modalError}</div>}
        <p className="wh-modal-text">Set the storage location. Status becomes STORED.</p>
        <WarehouseLocationFields value={loc} onChange={setLoc} />
        {locEmpty && <span className="wh-error-text">Add a location to store this item.</span>}
        <span className="wh-hint">Uppercased on save.</span>
      </ModalShell>

      <ModalShell isOpen={modal === 'move'} onClose={closeModal} title="Move Item"
        footer={modalFooter(doMove, 'Move', false, locEmpty)}>
        {modalError && <div className="wh-form-error">{modalError}</div>}
        <p className="wh-modal-text">New location. Status and dwell are unchanged.</p>
        <WarehouseLocationFields value={loc} onChange={setLoc} />
        {locEmpty && <span className="wh-error-text">Add a location for this move.</span>}
        <span className="wh-hint">Uppercased on save.</span>
      </ModalShell>

      <ModalShell isOpen={modal === 'ship'} onClose={closeModal} title="Ship Item"
        footer={modalFooter(doShip, 'Ship', true)}>
        {modalError && <div className="wh-form-error">{modalError}</div>}
        <div className="wh-field-row">
          <span className="wh-label wh-label-req">Disposition</span>
          <div className="wh-seg wh-seg-wrap">
            {SHIP_DISPOSITIONS.map((d) => (
              <button key={d} type="button" className={`wh-seg-btn${shipDisp === d ? ' wh-seg-active' : ''}`} onClick={() => setShipDisp(d)}>{d}</button>
            ))}
          </div>
        </div>
        <label className="wh-field-row">
          <span className="wh-label wh-label-req">Shipped Date</span>
          <input className="wh-input wh-input-narrow" type="date" value={shippedDate} onChange={(e) => setShippedDate(e.target.value)} />
        </label>
        <label className="wh-field-row">
          <span className="wh-label">Ship Ref</span>
          <input className="wh-input" type="text" value={shipRef} onChange={(e) => setShipRef(e.target.value)} placeholder="Optional" />
        </label>
      </ModalShell>
    </div>
  );
}
